import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  QrCode, MapPin, Navigation, CalendarCheck, Star, Trash2,
  Camera, Loader2, Building2, Stethoscope, Heart, Search, UserPlus, Share2
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import QRScannerModal from '@/components/QRScannerModal';
import useSmartQRScan from '@/hooks/useSmartQRScan';
import InviteYourDoctorQR from '@/components/InviteYourDoctorQR';
import MyRecoveryCard from '@/components/recovery/MyRecoveryCard';
import OfflineBanner from '@/components/OfflineBanner';
import bannerForDoctor from '@/lib/specialtyBanner';
import groupBySpecialty from '@/lib/groupBySpecialty';
import { buildRecommendUrl } from '@/lib/recommendShare';

// "My Clinics" — the patient's saved clinics from scanning doctor QR posters.
// Hard dependency on being logged in as a patient; the main App.js handles
// the auth redirect — we just render once `user` is loaded.
export default function MyClinicsPage() {
  const { user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScanner, setShowScanner] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/patient/saved-clinics');
      setClinics(data.clinics || []);
    } catch { /* suppressed */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { run: runSmartScan, saving: scanning } = useSmartQRScan({
    onSaved: (clinic) => {
      toast.success(`Saved ${clinic?.clinic_name || clinic?.doctor_name || 'clinic'} to your clinics.`);
      load();
    },
  });

  const handleScanResult = async (qrData) => {
    setShowScanner(false);
    await runSmartScan(qrData);
  };

  const remove = async (doctor_id) => {
    try {
      await api.delete(`/patient/saved-clinics/${doctor_id}`);
      setClinics(cs => cs.filter(c => c.doctor_id !== doctor_id));
      toast.success('Removed from your clinics');
    } catch { toast.error('Could not remove. Please try again.'); }
  };

  const toggleFav = async (c) => {
    try {
      const { data } = await api.patch(`/patient/saved-clinics/${c.doctor_id}/favorite`);
      setClinics(cs => cs.map(x => x.doctor_id === c.doctor_id ? { ...x, is_favorite: data.is_favorite } : x));
    } catch { /* no-op */ }
  };

  const directionsUrl = (c) => {
    const parts = [c.clinic_name, c.address, c.city, c.state, c.pincode, c.country].filter(Boolean).join(', ');
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="my-clinics-page">
      <Helmet><title>My Trusted Medical Clinics — DocSite</title></Helmet>
      <OfflineBanner />

      <section className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-3">
              <Heart size={12} /> {user?.name ? `Hi ${user.name.split(' ')[0]}` : 'Patient'}
            </div>
            <h1 className="text-3xl md:text-4xl font-medium text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Trusted Medical Clinics
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-xl">
              Every doctor whose QR you scan appears here. Tap to book again, get directions, or favourite it.
            </p>
          </div>
          <button onClick={() => setShowScanner(true)} disabled={scanning}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 shadow-sm"
                  data-testid="scan-qr-btn">
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            {scanning ? 'Saving…' : 'Scan Doctor’s QR'}
          </button>
        </div>

        <MyRecoveryCard />
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 size={14} className="animate-spin" /> Loading your clinics…
          </div>
        ) : clinics.length === 0 ? (
          <>
            <div className="rounded-3xl border-2 border-dashed border-slate-200 bg-white p-10 text-center mb-6" data-testid="my-clinics-empty">
              <div className="w-14 h-14 rounded-2xl bg-sky-50 text-sky-600 flex items-center justify-center mx-auto mb-4">
                <QrCode size={24} />
              </div>
              <h2 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No clinics saved yet
              </h2>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                Scan your doctor’s DocSite QR poster at their clinic. We’ll auto-save the clinic here so booking next time takes one tap.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                <button onClick={() => setShowScanner(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700"
                        data-testid="my-clinics-empty-scan-btn">
                  <Camera size={14} /> Scan your first clinic
                </button>
                <Link to="/insights"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-sky-300 hover:text-sky-700 transition-colors"
                      data-testid="my-clinics-empty-browse-btn">
                  <Search size={14} /> Browse Doctor Websites near you, powered by DocSite
                </Link>
                <Link to="/invite-your-doctor"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-semibold hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-colors"
                      data-testid="my-clinics-empty-invite-btn">
                  <UserPlus size={14} /> Invite your doctor
                </Link>
              </div>
            </div>
            <InviteYourDoctorQR defaultName={user?.name?.split(' ')[0] || ''} data-testid="my-clinics-invite-qr" />
          </>
        ) : (
          <>
            <div className="space-y-6 mb-8" data-testid="clinic-groups">
            {groupBySpecialty(clinics).map((group) => {
              const banner = bannerForDoctor([group.slug]);
              return (
                <section key={group.slug} className="rounded-3xl border border-slate-200 bg-white overflow-hidden" data-testid={`clinic-group-${group.slug}`}>
                  {/* Specialty banner header — one Nano-Banana anatomical
                      illustration shared by every doctor of this specialty
                      in the patient's network. DocSite™ watermark + doctor
                      count badge sit over the image. */}
                  {banner && (
                    <div className="relative w-full h-28 sm:h-32 bg-slate-900 overflow-hidden">
                      <img src={banner} alt="" loading="lazy" decoding="async"
                           width="1024" height="576"
                           className="absolute inset-0 w-full h-full object-cover object-center opacity-95"
                           onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/65 via-slate-900/15 to-transparent" />
                      <div className="absolute inset-0 flex items-end justify-between px-4 sm:px-5 pb-3">
                        <div className="text-white drop-shadow">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/75">Your trusted specialty</p>
                          <h3 className="text-base sm:text-lg font-semibold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{group.label}</h3>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <a
                            href={buildRecommendUrl({ specialtyLabel: group.label, doctors: group.doctors })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold shadow-sm hover:bg-emerald-600 transition-colors"
                            data-testid={`recommend-${group.slug}`}
                            title={`Recommend ${group.doctors.length === 1 ? 'this doctor' : 'these doctors'} to a friend on WhatsApp`}
                          >
                            <Share2 size={11} /> Recommend to friend
                          </a>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-semibold text-white/95">
                            {group.doctors.length} doctor{group.doctors.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </div>
                      <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900/55 backdrop-blur text-[9px] font-semibold tracking-[0.12em] text-white/90 uppercase">
                        DocSite<sup className="ml-0.5 text-[7px]">™</sup>
                      </span>
                    </div>
                  )}

                  {/* Mini doctor tiles — auto-fit so single-doctor groups
                      fill the row, two split 50/50, three or more wrap at
                      a 240px minimum per tile. */}
                  <div
                    className="grid gap-3 p-4"
                    style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
                    data-testid={`clinic-tiles-${group.slug}`}
                  >
                  {group.doctors.map((c) => (
                    <article key={c.doctor_id} className="rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-sky-200 transition-all" data-testid={`clinic-card-${c.doctor_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
                            <Stethoscope size={14} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.doctor_name}</p>
                            <p className="text-[10px] text-slate-500 truncate">{(c.specializations || []).slice(0, 2).join(' · ') || 'Doctor'}</p>
                          </div>
                        </div>
                        <button onClick={() => toggleFav(c)} aria-label="Toggle favourite"
                                className={`p-1 rounded-lg transition-colors ${c.is_favorite ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
                                data-testid={`fav-${c.doctor_id}`}>
                          <Star size={13} fill={c.is_favorite ? 'currentColor' : 'none'} />
                        </button>
                      </div>

                      <div className="flex items-start gap-1.5 text-[11px] text-slate-600 leading-snug min-h-[36px]">
                        <Building2 size={11} className="mt-0.5 flex-shrink-0 text-slate-400" />
                        <div className="min-w-0">
                          {c.clinic_name && <p className="font-medium text-slate-800 truncate">{c.clinic_name}</p>}
                          <p className="truncate">{[c.address, c.city, c.pincode].filter(Boolean).join(', ')}</p>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-1.5">
                        <Link to={`/dr/${c.doctor_slug}`} data-testid={`book-${c.doctor_id}`}
                              className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 transition-colors">
                          <CalendarCheck size={11} /> Book
                        </Link>
                        <a href={directionsUrl(c)} target="_blank" rel="noopener noreferrer" data-testid={`directions-${c.doctor_id}`}
                           className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-sky-600 text-white text-[11px] font-semibold hover:bg-sky-700 transition-colors">
                          <Navigation size={11} /> Directions
                        </a>
                        <button onClick={() => remove(c.doctor_id)} data-testid={`remove-${c.doctor_id}`}
                                className="px-2 py-1.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Remove clinic">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </article>
                  ))}
                  </div>
                </section>
              );
            })}
            </div>
            <InviteYourDoctorQR defaultName={user?.name?.split(' ')[0] || ''} />
          </>
        )}

        {/* Find a new doctor CTA */}
        {clinics.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
            <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600" data-testid="my-clinics-browse-websites">
              <Search size={13} /> Browse Doctor Websites near you, powered by DocSite
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/for" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600" data-testid="my-clinics-find-more">
              <MapPin size={13} /> Find another specialist in your city
            </Link>
          </div>
        )}
      </section>

      {showScanner && (
        <QRScannerModal onResult={handleScanResult} onClose={() => setShowScanner(false)} />
      )}
    </div>
  );
}
